import React, { useState, useRef, useEffect } from 'react';
import emailjs from '@emailjs/browser';

const ContactForm = () => {
    const form = useRef();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
        projectType: 'installation'
    });
    
    const [formStatus, setFormStatus] = useState({
        submitted: false,
        error: false,
        message: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [touched, setTouched] = useState({});

    // EmailJS configuration - using same service as configurator
    const SERVICE_ID = 'service_qgyi6fi'; // Same service as configurator
    const TEMPLATE_ID = 'template_p2fkvcd'; // New unified contact template
    const PUBLIC_KEY = 'F1ej8pHCFuoUZafzp';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
        
        // Clear validation errors when user types
        if (validationErrors[name]) {
            setValidationErrors({
                ...validationErrors,
                [name]: ''
            });
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({
            ...touched,
            [name]: true
        });
        validateField(name, formData[name]);
    };

    const validateField = (name, value) => {
        let errors = {...validationErrors};
        
        switch (name) {
            case 'name':
                if (!value.trim()) {
                    errors.name = 'Name is required';
                } else if (value.trim().length < 2) {
                    errors.name = 'Name must be at least 2 characters';
                } else {
                    errors.name = '';
                }
                break;
            case 'email':
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!value.trim()) {
                    errors.email = 'Email is required';
                } else if (!emailRegex.test(value)) {
                    errors.email = 'Please enter a valid email';
                } else {
                    errors.email = '';
                }
                break;
            case 'phone':
                const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
                if (value && !phoneRegex.test(value.replace(/\s/g, ''))) {
                    errors.phone = 'Please enter a valid phone number';
                } else {
                    errors.phone = '';
                }
                break;
            case 'subject':
                if (!value.trim()) {
                    errors.subject = 'Subject is required';
                } else if (value.trim().length < 5) {
                    errors.subject = 'Subject must be at least 5 characters';
                } else {
                    errors.subject = '';
                }
                break;
            case 'message':
                if (!value.trim()) {
                    errors.message = 'Message is required';
                } else if (value.trim().length < 20) {
                    errors.message = 'Message must be at least 20 characters';
                } else {
                    errors.message = '';
                }
                break;
            default:
                break;
        }
        
        setValidationErrors(errors);
        return !errors[name];
    };

    const validateForm = () => {
        let isValid = true;
        const requiredFields = ['name', 'email', 'subject', 'message'];
        
        // Touch all fields to show validation errors
        const newTouched = {};
        requiredFields.forEach(field => {
            newTouched[field] = true;
            if (!validateField(field, formData[field])) {
                isValid = false;
            }
        });
        
        // Also validate phone if it's filled in
        if (formData.phone) {
            newTouched.phone = true;
            if (!validateField('phone', formData.phone)) {
                isValid = false;
            }
        }
        
        setTouched({...touched, ...newTouched});
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form before submission
        if (!validateForm()) {
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            // Format the phone number for better readability in emails
            const formattedPhone = formData.phone ? formatPhoneNumber(formData.phone) : 'Not provided';
            
            // Create unified email data structure
            const createContactEmailData = (recipientEmail, emailType) => ({
                from_name: formData.name,
                from_email: formData.email,
                phone: formattedPhone,
                company: formData.company || 'Not provided',
                subject: formData.subject,
                message: formData.message,
                inquiry_type: getProjectTypeLabel(formData.projectType),
                to_email: recipientEmail,
                email_type: emailType, // This will determine template behavior
                date: new Date().toLocaleDateString(),
                support_email: "info@deeceeelevators.com",
                sales_contact: "+91 9837221980",
            });
            
            // Send email to company
            await emailjs.send(
                SERVICE_ID,
                TEMPLATE_ID,
                createContactEmailData('info@deeceeelevators.com', 'company_contact'),
                PUBLIC_KEY
            );

            await emailjs.send(
                SERVICE_ID,
                TEMPLATE_ID,
                createContactEmailData('info@deeceeelevators.in', 'company_contact'),
                PUBLIC_KEY
            );
            
            // Send auto-reply to customer
            await emailjs.send(
                SERVICE_ID,
                TEMPLATE_ID,
                createContactEmailData(formData.email, 'customer_autoreply'),
                PUBLIC_KEY
            );
            
            // Show success message
            setFormStatus({
                submitted: true,
                error: false,
                message: 'Your message has been sent successfully! You will receive a confirmation email shortly.'
            });
            
            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                subject: '',
                message: '',
                projectType: 'installation'
            });
            
        } catch (error) {
            console.error('Error submitting form:', error);
            
            setFormStatus({
                submitted: true,
                error: true,
                message: 'Something went wrong sending your message. Please try again or contact us directly by phone.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };
    
    // Helper function to format phone numbers
    const formatPhoneNumber = (phoneNumber) => {
        // Remove all non-digit characters
        const cleaned = phoneNumber.replace(/\D/g, '');
        
        // Format based on length
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
        } else {
            return phoneNumber; // Return original if can't format
        }
    };
    
    // Helper function to get readable project type labels
    const getProjectTypeLabel = (type) => {
        const labels = {
            'installation': 'New Installation',
            'maintenance': 'Maintenance & Service',
            'modernization': 'Modernization',
            'consultation': 'Consultation',
            'other': 'Other'
        };
        return labels[type] || type;
    };

    return (
        <div style={styles.formContainer}>
            <h2 style={styles.formTitle}>Send Us a Message</h2>
            
            {formStatus.submitted ? (
                <div 
                    style={formStatus.error ? styles.errorMessage : styles.successMessage}
                    className="animate__animated animate__fadeIn"
                >
                    {!formStatus.error && <i className="fas fa-check-circle" style={styles.statusIcon}></i>}
                    {formStatus.error && <i className="fas fa-exclamation-circle" style={styles.statusIcon}></i>}
                    <p style={styles.statusText}>{formStatus.message}</p>
                    
                    {!formStatus.error && (
                        <p style={styles.noteText}>
                            Our team will get back to you within 24 hours.
                        </p>
                    )}
                    
                    {formStatus.error && (
                        <button 
                            onClick={() => setFormStatus({submitted: false, error: false, message: ''})}
                            style={styles.retryButton}
                        >
                            Try Again
                        </button>
                    )}
                </div>
            ) : (
                <form ref={form} onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGrid}>
                        {/* Name field */}
                        <div style={styles.formGroup}>
                            <label htmlFor="name" style={styles.label}>
                                Full Name <span style={styles.requiredMark}>*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                aria-required="true"
                                aria-invalid={touched.name && !!validationErrors.name}
                                style={{
                                    ...styles.input,
                                    ...(touched.name && validationErrors.name ? styles.inputError : {})
                                }}
                                placeholder="Your name"
                            />
                            {touched.name && validationErrors.name && (
                                <p style={styles.errorText}>{validationErrors.name}</p>
                            )}
                        </div>
                        
                        {/* Email field */}
                        <div style={styles.formGroup}>
                            <label htmlFor="email" style={styles.label}>
                                Email Address <span style={styles.requiredMark}>*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                aria-required="true"
                                aria-invalid={touched.email && !!validationErrors.email}
                                style={{
                                    ...styles.input,
                                    ...(touched.email && validationErrors.email ? styles.inputError : {})
                                }}
                                placeholder="your.email@example.com"
                            />
                            {touched.email && validationErrors.email && (
                                <p style={styles.errorText}>{validationErrors.email}</p>
                            )}
                        </div>
                        
                        {/* Phone field */}
                        <div style={styles.formGroup}>
                            <label htmlFor="phone" style={styles.label}>
                                Phone Number
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                aria-invalid={touched.phone && !!validationErrors.phone}
                                style={{
                                    ...styles.input,
                                    ...(touched.phone && validationErrors.phone ? styles.inputError : {})
                                }}
                                placeholder="Your phone number"
                            />
                            {touched.phone && validationErrors.phone && (
                                <p style={styles.errorText}>{validationErrors.phone}</p>
                            )}
                        </div>
                        
                        {/* Company field */}
                        <div style={styles.formGroup}>
                            <label htmlFor="company" style={styles.label}>
                                Company / Organization
                            </label>
                            <input
                                type="text"
                                id="company"
                                name="company"
                                value={formData.company}
                                onChange={handleChange}
                                style={styles.input}
                                placeholder="Your company name"
                            />
                        </div>
                        
                        {/* Project type select */}
                        <div style={styles.formGroup}>
                            <label htmlFor="projectType" style={styles.label}>
                                Inquiry Type <span style={styles.requiredMark}>*</span>
                            </label>
                            <div style={styles.selectWrapper}>
                                <select 
                                    id="projectType" 
                                    name="projectType" 
                                    value={formData.projectType}
                                    onChange={handleChange}
                                    required
                                    aria-required="true"
                                    style={styles.select}
                                >
                                    <option value="installation">New Installation</option>
                                    <option value="maintenance">Maintenance & Service</option>
                                    <option value="modernization">Modernization</option>
                                    <option value="consultation">Consultation</option>
                                    <option value="other">Other</option>
                                </select>
                                <div style={styles.selectArrow}></div>
                            </div>
                        </div>
                        
                        {/* Subject field */}
                        <div style={styles.formGroup}>
                            <label htmlFor="subject" style={styles.label}>
                                Subject <span style={styles.requiredMark}>*</span>
                            </label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                required
                                aria-required="true"
                                aria-invalid={touched.subject && !!validationErrors.subject}
                                style={{
                                    ...styles.input,
                                    ...(touched.subject && validationErrors.subject ? styles.inputError : {})
                                }}
                                placeholder="Inquiry subject"
                            />
                            {touched.subject && validationErrors.subject && (
                                <p style={styles.errorText}>{validationErrors.subject}</p>
                            )}
                        </div>
                    </div>
                    
                    {/* Message field - full width */}
                    <div style={styles.formGroup}>
                        <label htmlFor="message" style={styles.label}>
                            Message <span style={styles.requiredMark}>*</span>
                        </label>
                        <textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required
                            aria-required="true"
                            aria-invalid={touched.message && !!validationErrors.message}
                            style={{
                                ...styles.textarea,
                                ...(touched.message && validationErrors.message ? styles.inputError : {})
                            }}
                            placeholder="Please describe your requirements or questions..."
                            rows={5}
                        />
                        {touched.message && validationErrors.message && (
                            <p style={styles.errorText}>{validationErrors.message}</p>
                        )}
                    </div>
                    
                    <div style={styles.formFooter}>
                        {/* <p style={styles.formDisclaimer}>
                            By submitting this form, you agree to our <a href="/privacy-policy" style={styles.link}>Privacy Policy</a>.
                            Your information will not be shared with third parties.
                        </p> */}
                        
                        <button 
                            type="submit" 
                            style={styles.submitButton}
                            disabled={isSubmitting}
                            aria-disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <span style={styles.buttonContent}>
                                    <i className="fas fa-spinner fa-spin" style={styles.spinnerIcon}></i>
                                    Sending...
                                </span>
                            ) : (
                                <span style={styles.buttonContent}>
                                    <i className="fas fa-paper-plane" style={styles.buttonIcon}></i>
                                    Send Message
                                </span>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

const styles = {
    formContainer: {
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 5px 30px rgba(0, 0, 0, 0.05)',
        padding: '30px',
        border: '1px solid rgba(33, 150, 243, 0.1)',
    },
    formTitle: {
        fontSize: '1.8rem',
        fontWeight: 600,
        marginBottom: '25px',
        color: '#0d1b2a',
        fontFamily: "'Inter', sans-serif",
    },
    form: {
        width: '100%',
    },
    formGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
    },
    formGroup: {
        marginBottom: '20px',
        position: 'relative',
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontSize: '0.95rem',
        fontWeight: 500,
        color: '#333',
    },
    requiredMark: {
        color: '#e53935',
        marginLeft: '2px',
    },
    input: {
        width: '100%',
        padding: '12px 15px',
        fontSize: '1rem',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#fff',
        transition: 'all 0.3s ease',
        outline: 'none',
    },
    inputError: {
        border: '1px solid #e53935',
        backgroundColor: 'rgba(229, 57, 53, 0.05)',
    },
    errorText: {
        color: '#e53935',
        fontSize: '0.8rem',
        marginTop: '5px',
        position: 'absolute',
    },
    selectWrapper: {
        position: 'relative',
    },
    select: {
        width: '100%',
        padding: '12px 15px',
        fontSize: '1rem',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#fff',
        transition: 'all 0.3s ease',
        appearance: 'none',
        outline: 'none',
        cursor: 'pointer',
    },
    selectArrow: {
        position: 'absolute',
        right: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '0',
        height: '0',
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        borderTop: '6px solid #555',
        pointerEvents: 'none',
    },
    textarea: {
        width: '100%',
        padding: '12px 15px',
        fontSize: '1rem',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#fff',
        transition: 'all 0.3s ease',
        minHeight: '120px',
        resize: 'vertical',
        outline: 'none',
    },
    formFooter: {
        marginTop: '60px',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '20px',
    },
    formDisclaimer: {
        fontSize: '0.85rem',
        color: '#666',
        flex: '1 1 300px',
        margin: 0,
    },
    link: {
        color: '#1976D2',
        textDecoration: 'none',
    },
    submitButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#1976D2',
        color: 'white',
        border: 'none',
        padding: '10px 30px ',
        height: '50px',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 5px 15px rgba(25, 118, 210, 0.2)',
        flex: '0 0 auto',
        minWidth: '180px',
    },
    buttonContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    buttonIcon: {
        fontSize: '1rem',
    },
    spinnerIcon: {
        fontSize: '1rem',
    },
    successMessage: {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
        padding: '30px 20px',
        borderRadius: '8px',
        textAlign: 'center',
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    errorMessage: {
        backgroundColor: '#ffebee',
        color: '#c62828',
        padding: '30px 20px',
        borderRadius: '8px',
        textAlign: 'center',
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    statusIcon: {
        fontSize: '3rem',
        marginBottom: '15px',
    },
    statusText: {
        fontWeight: 500,
        fontSize: '1.1rem',
    },
    noteText: {
        marginTop: '15px',
        fontSize: '0.9rem',
        opacity: 0.8,
    },
    retryButton: {
        backgroundColor: '#f44336',
        color: 'white',
        border: 'none',
        padding: '10px 20px',
        borderRadius: '5px',
        marginTop: '15px',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'all 0.3s ease',
    },
};

export default ContactForm;